import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown, LayoutDashboard, LogOut, Scale, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';

const NAV = [
  { to: '/advogados', label: 'Advogados' },
  { to: '/cidades', label: 'Cidades' },
  { to: '/categorias', label: 'Áreas do Direito' },
  { to: '/artigos', label: 'Blog' },
  { to: '/planos', label: 'Planos', testId: 'header-planos-link' },
];

const PARENT_URL = 'https://ambitojuridico.com.br/';

const navCls = ({ isActive }) =>
  `inline-flex items-center min-h-[48px] text-[16px] font-semibold underline underline-offset-[0.18em] decoration-[0.075em] transition-colors ${isActive ? 'text-[#063f4c] decoration-[0.13em]' : 'text-[#075a6d] hover:text-[#063f4c]'}`;

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { user, firm, logout } = useAuth();
  const close = () => setMobileOpen(false);

  return (
    <header className="bg-white border-b border-[#cbd8dc]" id="top" data-testid="site-header">
      <div className="aj-wrap flex items-center justify-between gap-7 py-5 md:py-6">
        <Link to="/" className="aj-wordmark" aria-label="Ambito Advogados — página inicial" data-testid="header-logo">
          Ambito<br />Advogados
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Navegação principal">
          {NAV.map(item => (
            <NavLink key={item.to} to={item.to} className={navCls} data-testid={item.testId}>{item.label}</NavLink>
          ))}
          <a href={PARENT_URL} target="_blank" rel="noopener" className="inline-flex items-center gap-1 min-h-[48px] text-[16px] font-semibold text-[#075a6d] hover:text-[#063f4c] underline underline-offset-[0.18em] decoration-[0.075em]" data-testid="header-parent-link">
            Conteúdos jurídicos <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="aj-button aj-sm" data-testid="header-user-menu">
                  <span className="max-w-[140px] truncate">{firm?.name || user.email}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 border-[#cbd8dc]">
                {user.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="header-admin-link">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Painel administrativo
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/painel')} data-testid="header-dashboard-link">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Painel do escritório
                </DropdownMenuItem>
                {firm && (
                  <DropdownMenuItem onClick={() => navigate(`/escritorio/${firm.slug}`)}>
                    <Scale className="w-4 h-4 mr-2" /> Ver meu perfil público
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="header-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className={navCls({ isActive: false })} data-testid="header-login-link">Entrar</Link>
              <Link to="/cadastro" className="aj-button" data-testid="header-register-link">Cadastrar escritório</Link>
            </>
          )}
        </nav>

        <button
          className={`lg:hidden min-h-[48px] px-4 border border-[#547680] rounded-[7px] text-[16px] text-[#153e44] ${mobileOpen ? 'bg-[#edf4f5]' : 'bg-white'}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
          data-testid="header-mobile-toggle"
        >
          {mobileOpen ? 'Fechar' : 'Menu'}
        </button>
      </div>

      {mobileOpen && (
        <nav id="mobile-nav" className="lg:hidden aj-wrap pb-5 aj-fade" aria-label="Navegação mobile" data-testid="header-mobile-nav">
          <div className="border border-[#cbd8dc] rounded-[8px] p-3 bg-white">
            {NAV.map(item => (
              <Link key={item.to} to={item.to} onClick={close} className="block py-3 px-2 text-[17px] font-semibold text-[#075a6d] underline underline-offset-[0.18em] border-b border-[#e1e9eb]">{item.label}</Link>
            ))}
            <a href={PARENT_URL} target="_blank" rel="noopener" className="block py-3 px-2 text-[17px] font-semibold text-[#075a6d] underline underline-offset-[0.18em] border-b border-[#e1e9eb]">Conteúdos jurídicos ↗</a>
            {user ? (
              <>
                {user.role === 'admin' && <Link to="/admin" onClick={close} className="block py-3 px-2 text-[17px] font-semibold text-[#075a6d] underline border-b border-[#e1e9eb]">Painel administrativo</Link>}
                <Link to="/painel" onClick={close} className="block py-3 px-2 text-[17px] font-semibold text-[#075a6d] underline border-b border-[#e1e9eb]">Painel do escritório</Link>
                <button onClick={() => { logout(); close(); navigate('/'); }} className="block w-full text-left py-3 px-2 text-[17px] font-semibold text-[#075a6d] underline">Sair</button>
              </>
            ) : (
              <div className="flex flex-col gap-2 pt-3">
                <Link to="/login" onClick={close} className="aj-button">Entrar</Link>
                <Link to="/cadastro" onClick={close} className="aj-button aj-primary">Cadastrar escritório</Link>
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
